import React from 'react';
import { Helmet } from 'react-helmet-async';
import { FaUtensils, FaCheckCircle, FaArrowLeft, FaPhone } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import useScrollAnimation from '../../../hooks/useScrollAnimation';
import Footer from '../../layout/Footer';
import './ServicePage.css';

const KitchenRemodelingPage = () => {
  const navigate = useNavigate();
  const titleRef = useScrollAnimation();
  const featuresRef = useScrollAnimation();

  const features = [
    'Custom Cabinet Design & Installation',
    'Granite, Quartz & Marble Countertops',
    'Modern Appliance Integration',
    'Kitchen Island Design',
    'Backsplash Installation',
    'Lighting Design & Installation',
    'Plumbing & Electrical Updates',
    'Flooring Installation'
  ];

  const processSteps = [
    {
      step: 1,
      title: 'Initial Consultation',
      description: 'We visit your home to assess your space, discuss your vision, and understand your budget and timeline.'
    },
    {
      step: 2,
      title: 'Design & Planning',
      description: 'Our team creates detailed plans and 3D renderings of your new kitchen, including material selections.'
    },
    {
      step: 3,
      title: 'Permits & Preparation',
      description: 'We handle all necessary permits and prepare your space for construction with protective measures.'
    },
    {
      step: 4,
      title: 'Construction',
      description: 'Our skilled craftsmen execute the renovation with daily updates and quality control checks.'
    },
    {
      step: 5,
      title: 'Final Inspection',
      description: 'We conduct a thorough walkthrough with you to ensure everything meets our high standards.'
    }
  ];

  const scrollToContact = () => {
    navigate('/', { replace: true });
    setTimeout(() => {
      const contactSection = document.getElementById('contact');
      if (contactSection) {
        contactSection.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };


  return (
    <>
      <Helmet>
        <title>RC Roofing - Kitchen Remodeling</title>
        <meta name="description" content="Transform your kitchen with RC Roofing's professional kitchen remodeling services. Custom cabinets, countertops, and complete kitchen renovations. Free estimates available." />
        <meta name="keywords" content="kitchen remodeling, kitchen renovation, custom kitchen cabinets, kitchen countertops, kitchen design, kitchen contractor" />
        <link rel="canonical" href="https://rcroofing.com/services/kitchen-remodeling" />
        
        {/* Open Graph */}
        <meta property="og:title" content="Kitchen Remodeling Services | RC Roofing" />
        <meta property="og:description" content="Transform your kitchen with RC Roofing's professional kitchen remodeling services. Custom cabinets, countertops, and complete kitchen renovations." />
        <meta property="og:url" content="https://rcroofing.com/services/kitchen-remodeling" />
        <meta property="og:image" content="https://rcroofing.com/images/banner/hero1.webp" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Kitchen Remodeling Services | RC Roofing" />
        <meta name="twitter:description" content="Transform your kitchen with RC Roofing's professional kitchen remodeling services. Custom cabinets, countertops, and complete kitchen renovations." />
        <meta name="twitter:image" content="https://rcroofing.com/images/banner/hero1.webp" />
      </Helmet>

      <div className="service-page">
        {/* Navigation */}
        <nav className="service-nav">
          <div className="container">
            <button 
              className="back-button"
              onClick={() => navigate('/')}
            >
              <FaArrowLeft /> Back to Home
            </button>
            <button 
              className="contact-button"
              onClick={scrollToContact}
            >
              <FaPhone /> Get Free Quote
            </button>
          </div>
        </nav>

        {/* Hero Section */}
        <section className="service-hero">
          <div className="service-hero-background">
            <img src="/images/banner/hero1.webp" alt="Professional kitchen remodeling by RC Roofing" />
            <div className="service-hero-overlay"></div>
          </div>
          <div className="container">
            <div className="service-hero-content">
              <div className="service-icon">
                <FaUtensils />
              </div>
              <h1 ref={titleRef} className="service-hero-title">
                Kitchen Remodeling Services
              </h1>
              <p className="service-hero-description">
                Transform your kitchen into the heart of your home with our expert remodeling services. 
                From custom cabinetry to modern appliances, we create kitchens that combine style, 
                functionality, and lasting value.
              </p>
              <div className="service-hero-stats">
                <div className="stat">
                  <strong>200+</strong>
                  <span>Kitchens Remodeled</span>
                </div>
                <div className="stat">
                  <strong>4-8 weeks</strong>
                  <span>Typical Timeline</span>
                </div>
                <div className="stat">
                  <strong>2 years</strong>
                  <span>Workmanship Warranty</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="service-features">
          <div className="container">
            <h2>What's Included in Our Kitchen Remodeling</h2>
            <div ref={featuresRef} className="features-grid">
              {features.map((feature, index) => (
                <div key={index} className="feature-item">
                  <FaCheckCircle className="feature-icon" />
                  <span>{feature}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Process Section */}
        <section className="service-process">
          <div className="container">
            <h2>Our Kitchen Remodeling Process</h2>
            <div className="process-steps">
              {processSteps.map((step, index) => (
                <div key={index} className="process-step">
                  <div className="step-number">{step.step}</div>
                  <div className="step-content">
                    <h3>{step.title}</h3>
                    <p>{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="service-cta">
          <div className="container">
            <div className="cta-content">
              <h2>Ready to Transform Your Kitchen?</h2>
              <p>
                Get a free, detailed estimate for your kitchen remodeling project. 
                Our team will work with you to create the kitchen of your dreams within your budget.
              </p>
              <div className="cta-buttons">
                <button className="btn btn-primary" onClick={scrollToContact}>
                  Get Free Estimate
                </button>
                <button className="btn btn-outline" onClick={() => navigate('/gallery')}>
                  View Our Work
                </button>
              </div>
            </div>
          </div>
        </section>
      </div>
      <Footer />
    </>
  );
};

export default KitchenRemodelingPage;
